import DOMPurify from "dompurify";
import { html } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import {
  resolveSafeSameOriginEmbedUrl,
  type SafeSameOriginEmbedResolution,
} from "../open-external-url.ts";
import type { ChatSidebarBlock } from "../types/chat-types.ts";

type SvgBlock = Extract<ChatSidebarBlock, { type: "svg" }>;
type IframeBlock = Extract<ChatSidebarBlock, { type: "iframe" }>;
export type RichStaticBlockContext = "inline" | "sidebar";

const SVG_ALLOWED_TAGS = [
  "svg",
  "g",
  "path",
  "circle",
  "ellipse",
  "rect",
  "line",
  "polyline",
  "polygon",
  "text",
  "tspan",
  "defs",
  "clipPath",
  "linearGradient",
  "radialGradient",
  "stop",
  "title",
  "desc",
];
const SVG_ALLOWED_ATTR = [
  "aria-hidden",
  "class",
  "clip-path",
  "cx",
  "cy",
  "d",
  "dx",
  "dy",
  "fill",
  "fill-opacity",
  "focusable",
  "font-family",
  "font-size",
  "font-weight",
  "gradientTransform",
  "gradientUnits",
  "height",
  "id",
  "offset",
  "opacity",
  "points",
  "preserveAspectRatio",
  "r",
  "role",
  "rx",
  "ry",
  "stop-color",
  "stop-opacity",
  "stroke",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-miterlimit",
  "stroke-opacity",
  "stroke-width",
  "text-anchor",
  "transform",
  "viewBox",
  "width",
  "x",
  "x1",
  "x2",
  "xmlns",
  "y",
  "y1",
  "y2",
];
const SVG_FORBIDDEN_TAGS = ["script", "foreignObject", "iframe", "object", "embed", "audio", "video", "canvas", "style", "image", "use"];
const SVG_FORBIDDEN_ATTRS = ["href", "xlink:href"];

export const SAME_ORIGIN_EMBED_SANDBOX = "allow-forms allow-same-origin allow-scripts";
export const SAME_ORIGIN_EMBED_ALLOW = "clipboard-read; clipboard-write";

export function sanitizeSvgMarkup(rawSvg: string): string | null {
  const candidate = rawSvg.trim();
  if (!candidate) {
    return null;
  }
  const sanitized = DOMPurify.sanitize(candidate, {
    ALLOWED_TAGS: SVG_ALLOWED_TAGS,
    ALLOWED_ATTR: SVG_ALLOWED_ATTR,
    FORBID_TAGS: SVG_FORBIDDEN_TAGS,
    FORBID_ATTR: SVG_FORBIDDEN_ATTRS,
  });
  if (typeof sanitized !== "string") {
    return null;
  }
  const normalized = sanitized.trim();
  return normalized.toLowerCase().startsWith("<svg") ? normalized : null;
}

function getEmbedResolution(block: IframeBlock): SafeSameOriginEmbedResolution {
  if (typeof window === "undefined") {
    return {
      status: "blocked",
      url: null,
      reason: "Interactive embeds require a browser context.",
    };
  }
  return resolveSafeSameOriginEmbedUrl(block.url, window.location.href);
}

export function renderRichSvgBlock(block: SvgBlock, context: RichStaticBlockContext) {
  const title = block.label ?? "SVG";
  const sanitized = sanitizeSvgMarkup(block.content);
  return html`
    <section class=${[
      "chat-rich-static",
      `chat-rich-static--${context}`,
      "chat-rich-static--svg",
      sanitized ? "" : "chat-rich-static--blocked",
    ]
      .filter(Boolean)
      .join(" ")}>
      <div class="chat-rich-static__header">
        <div class="chat-rich-static__title">${title}</div>
        <div class="chat-rich-static__meta">${sanitized ? "Sanitized SVG preview" : "SVG preview unavailable"}</div>
      </div>
      ${sanitized
        ? html`<div class="chat-rich-static__stage chat-rich-static__stage--svg">${unsafeHTML(sanitized)}</div>`
        : html`<div class="chat-rich-static__notice">SVG content was blocked by the safe render policy.</div>`}
      <details class="chat-rich-static__source" ?open=${!sanitized}>
        <summary class="chat-rich-static__source-summary">View source</summary>
        <pre class="chat-rich-static__source-code"><code>${block.content}</code></pre>
      </details>
    </section>
  `;
}

export function renderRichIframeBlock(block: IframeBlock, context: RichStaticBlockContext) {
  const title = block.label ?? (block.variant === "h5" ? "H5" : "Iframe");
  const resolution = getEmbedResolution(block);
  return html`
    <section class=${[
      "chat-rich-static",
      `chat-rich-static--${context}`,
      "chat-rich-static--iframe",
      resolution.status === "allowed" ? "" : "chat-rich-static--blocked",
    ]
      .filter(Boolean)
      .join(" ")}>
      <div class="chat-rich-static__header">
        <div class="chat-rich-static__title">${title}</div>
        <div class="chat-rich-static__meta">
          ${resolution.status === "allowed" ? "Same-origin interactive preview" : resolution.reason}
        </div>
      </div>
      ${resolution.status === "allowed"
        ? html`
            <div class="chat-rich-static__frame-wrap">
              <iframe
                class="chat-rich-static__iframe"
                src=${resolution.url}
                title=${title}
                loading="lazy"
                referrerpolicy="same-origin"
                sandbox=${SAME_ORIGIN_EMBED_SANDBOX}
                allow=${SAME_ORIGIN_EMBED_ALLOW}
              ></iframe>
            </div>
          `
        : html`<div class="chat-rich-static__notice">${resolution.reason}</div>`}
      <details class="chat-rich-static__source" ?open=${resolution.status !== "allowed"}>
        <summary class="chat-rich-static__source-summary">View source</summary>
        <pre class="chat-rich-static__source-code"><code>${block.url}</code></pre>
      </details>
    </section>
  `;
}
